import { useEffect, useRef } from "react";

import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectActiveClassSessionId,
  selectClassUserIdsWithIsVisibleInClassCapability,
  selectCurrentClassUserId,
  selectIsClassSessionVirtual,
} from "store/selectors";
import {
  selectAvActiveClassUserIds,
  selectClassUserIdsPresentInClass,
  selectIsStateParticipantBarLeader,
} from "store/selectors/pubsub-virtual-class-state";
import {
  selectCalculatedListForActiveParticipants,
  selectIsCalculatedParticipantBarLeader,
  selectPresentClassUserIdsWithIsVisibleInClassCapability,
} from "store/selectors/virtual-class-users-sorting";
import { useCountSelector, useListSelector } from "store/store-hooks";
import { useAv, useAvPubsubMessaging } from "utils/av";
import { logInfoWithDebugFlag } from "utils/logger";

export const useDistributedParticipantBarObserver = () => {
  const { canAuthorSection } = useCapabilities();
  const { isInVirtualClass } = useAv();
  const isCalculatedParticipantBarLeader = useAppSelector(selectIsCalculatedParticipantBarLeader);
  const isStateParticipantBarLeader = useAppSelector(selectIsStateParticipantBarLeader);
  const participantBarList = useListSelector(selectAvActiveClassUserIds);
  const calculatedList = useListSelector(selectCalculatedListForActiveParticipants);
  const isClassSessionVirtual = useAppSelector(selectIsClassSessionVirtual);
  const activeClassSessionId = useAppSelector(selectActiveClassSessionId);
  const isValidClassSession = !!activeClassSessionId && isClassSessionVirtual;
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);

  const presentClassUserIdCount = useCountSelector(selectClassUserIdsPresentInClass);
  const visibleInVirtualClassCount = useCountSelector(
    selectClassUserIdsWithIsVisibleInClassCapability,
  );
  const presentAndVisibleInClassCount = useCountSelector(
    selectPresentClassUserIdsWithIsVisibleInClassCapability,
  );
  // Already subscribed via calculatedList; length is enough for the debug counts log.
  const calculatedListCount = calculatedList.length;
  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();

  const lastPublishedListRef = useRef<string>("");

  // Debug log pipeline sizes when this is the calculated leader (re-runs when a count changes).
  useEffect(() => {
    if (!isValidClassSession || !isCalculatedParticipantBarLeader) return;
    logInfoWithDebugFlag(
      "debug_virtual_class",
      "[useDistributedParticipantBarObserver] Leader Pipeline counts",
      {
        virtualClassStatePresentClassUserIdCount: presentClassUserIdCount,
        visibleInVirtualClassCount,
        presentAndVisibleInClassCount,
        calculatedListForActiveParticipantsCount: calculatedListCount,
      },
    );
  }, [
    isValidClassSession,
    isCalculatedParticipantBarLeader,
    presentClassUserIdCount,
    visibleInVirtualClassCount,
    presentAndVisibleInClassCount,
    calculatedListCount,
  ]);

  useEffect(() => {
    if (
      !isInVirtualClass ||
      !canAuthorSection ||
      !isCalculatedParticipantBarLeader ||
      !isValidClassSession
    ) {
      return;
    }

    if (currentClassUserId && !isStateParticipantBarLeader) {
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[useDistributedParticipantBarObserver] Updating leader",
        currentClassUserId,
      );
      sendPubsubVirtualClassStateMessage({ action: "update-leader" });
    }
  }, [
    canAuthorSection,
    isCalculatedParticipantBarLeader,
    isValidClassSession,
    sendPubsubVirtualClassStateMessage,
    isInVirtualClass,
    currentClassUserId,
    isStateParticipantBarLeader,
  ]);

  useEffect(() => {
    if (!canAuthorSection || !isStateParticipantBarLeader || !isValidClassSession) {
      lastPublishedListRef.current = "";
      return;
    }

    const listKey = JSON.stringify(calculatedList);
    if (listKey === lastPublishedListRef.current) return;

    const isSameAsCurrent =
      participantBarList.length === calculatedList.length &&
      participantBarList.every((id, index) => id === calculatedList[index]);
    // Avoid publishing the same list twice even if the remote list has not been updated yet
    if (isSameAsCurrent) {
      lastPublishedListRef.current = listKey;
      return;
    }

    logInfoWithDebugFlag(
      "debug_virtual_class",
      "[useDistributedParticipantBarObserver] Publishing av active list",
      { av_active_class_user_ids: calculatedList },
    );
    sendPubsubVirtualClassStateMessage({
      action: "update-av-active-list",
      av_active_class_user_ids: calculatedList,
    });
    lastPublishedListRef.current = listKey;
  }, [
    canAuthorSection,
    isStateParticipantBarLeader,
    calculatedList,
    participantBarList,
    isValidClassSession,
    sendPubsubVirtualClassStateMessage,
  ]);
};
